import React, { useState } from "react";
import { Button } from "@/components/ui/button/button";
import { BackspaceIcon } from "../../ui/icons";
import { cn } from "@/lib/utils";

type Props = {
	pin: string;
	setPin: (value: string) => void;
	disabled?: boolean;
};

const pressedStyle = `
  .pin-key-pressed {
    transform: scale(0.92);
    transition: transform 0.12s cubic-bezier(0.4,0,0.2,1);
    background-color: rgba(0,0,0,0.04);
  }
`;

const PinKeyboard: React.FC<Props> = ({ pin, setPin, disabled = false }) => {
	const [pressedKey, setPressedKey] = useState<string | null>(null);

	const handleNumberClick = (num: string) => {
		if (pin.length < 4) {
			setPin(pin + num);
		}
	};

	const handleKeyPress = (key: string, cb: () => void) => {
		setPressedKey(key);
		cb();
		setTimeout(() => setPressedKey(null), 120);
	};

	const handleBackspace = () => {
		setPin(pin.slice(0, -1));
	};

	const handleReset = () => {
		setPin("");
	};
	return (
		<>
			<style>{pressedStyle}</style>
			<div className="flex gap-4">
				{[0, 1, 2, 3].map((i) => (
					<div
						key={i}
						className={cn(
							"w-4 h-4 rounded-full",
							i < pin.length ? "bg-primary-600" : "bg-gray-200",
						)}
					/>
				))}
			</div>

			<div className="flex flex-col items-center w-full max-w-xs mt-6">
				<div className="grid flex-1 w-full grid-cols-3 gap-0">
					{[..."123456789000"].map((num, i) => {
						if (i === 9) return <div key="empty" />;
						if (i === 11) {
							return (
								<Button
									variant="unstyled"
									type="button"
									key="backspace"
									onClick={() => handleKeyPress("backspace", handleBackspace)}
									disabled={disabled}
									className={cn(
										"flex items-center justify-center w-full text-lg font-semibold aspect-video [&_svg]:size-6",
										pressedKey === "backspace" && "pin-key-pressed",
									)}
								>
									<BackspaceIcon className="w-6 h-6 fill-primary-600" />
								</Button>
							);
						}
						return (
							<div key={num} className="col-span-1">
								<Button
									variant="unstyled"
									type="button"
									key={num}
									onClick={() =>
										handleKeyPress(num, () => handleNumberClick(num))
									}
									disabled={disabled}
									className={cn(
										"flex items-center justify-center w-full text-lg font-semibold aspect-video",
										pressedKey === num && "pin-key-pressed",
									)}
								>
									{num}
								</Button>
							</div>
						);
					})}
				</div>

				{/* Reset */}
				<Button
					variant="unstyled"
					onClick={() => handleKeyPress("reset", handleReset)}
					disabled={disabled}
					type="button"
					className={cn(
						"mt-4 text-sm font-medium text-textDark",
						pressedKey === "reset" && "pin-key-pressed",
					)}
				>
					Reset
				</Button>
			</div>
		</>
	);
};

export default PinKeyboard;
